import { useEffect, useState } from 'react'
import useGridConfig from '../hooks/use-grid-config'
import buildGridColumn, { Column } from '../utils/build-grid-column'
import { useWindowSize } from 'react-use'
import { useAnimationFrame } from 'framer-motion'
import STAFF from '@/app/config/staff'
import { shuffle } from 'lodash'

const useMarqueeColumns = () => {
    const gridConfig = useGridConfig()
    const { width: viewportWidth } = useWindowSize()

    const [columns, setColumns] = useState<Column[]>([])
    const [lastUsedStaffIndex, setLastUsedStaffIndex] = useState(0)
    const [columnCounter, setColumnCounter] = useState(0)
    const [x, setX] = useState(0)

    /**
     * Initialize columns based on the grid configuration and viewport width.
     * This will create the initial set of columns with shuffled staff members.
     * The number of columns is determined by the `initialColumns` property in the grid configuration.
     * The staff members are shuffled to ensure a random order each time the component mounts.
     */
    useEffect(() => {
        const shuffled = shuffle(STAFF)
        let staffIdx = 0
        let colIdx = 0
        const initial: Column[] = []

        while (initial.length < gridConfig.initialColumns) {
            const col = buildGridColumn(gridConfig, shuffled, staffIdx, colIdx)
            initial.push(col)
            staffIdx = col.usedUpToStaff
            colIdx++
        }

        setColumns(initial)
        setLastUsedStaffIndex(staffIdx)
        setColumnCounter(colIdx)
        setX(0)
    }, [gridConfig, viewportWidth])

    useAnimationFrame(() => {
        setX((prev) => prev - gridConfig.speed)
    })

    /**
     * This effect checks if the first column has scrolled out of view.
     * If it has, it removes the first column and appends a new one to the end of the columns array.
     * The new column is built using the `buildGridColumn` function, which takes the current grid configuration,
     * the staff members, the last used staff index, and the current column counter.
     * The last used staff index is updated to point to the next staff member,
     * and the column counter is incremented.
     */
    useEffect(() => {
        if (!columns.length) return

        const first = columns[0]
        const triggerDistance = first.width + gridConfig.gutter

        if (Math.abs(x) >= triggerDistance) {
            setX((prev) => prev + first.width + gridConfig.gutter)

            const nextCol = buildGridColumn(gridConfig, STAFF, lastUsedStaffIndex, columnCounter)
            setColumns((cols) => {
                const [, ...rest] = cols
                return [...rest, nextCol]
            })
            setLastUsedStaffIndex((i) => (i + nextCol.stack.length) % STAFF.length)
            setColumnCounter((c) => c + 1)
        }
    }, [x, columns, gridConfig, lastUsedStaffIndex, columnCounter])

    return { columns, x, config: gridConfig }
}

export default useMarqueeColumns
